<script setup lang="ts">
import { computed } from 'vue'
import {
  faClock,
  faCopy,
  faGear,
  faPen,
  faPlus,
  faSquareDashedCirclePlus,
  faTrash,
  faUpDown,
} from '@fortawesome/pro-light-svg-icons'
import DropDownActions from '@consumer/components/DropDownActions/DropDownActions.vue'
import type { DropDownActionGroup, DropDownActionItem } from '@consumer/components/DropDownActions/DropDownActions.vue'
import GIcon from '@consumer/components/GIcon.vue'
import type { AdminConsumerHeroImageIndex, AdminConsumerHeroSectionIndex } from '@admin/types/serializers'
import type { AdminConsumerHeroSectionItem } from '@admin/serializers'
import * as heroImageDesign from '@admin/logic/heroImageDesign'
import * as heroSectionLogic from '@admin/logic/consumer/heroSection'
import * as heroSectionItemLogic from '@admin/logic/consumer/heroSectionItem'

withDefaults(defineProps<{
  heroImage: AdminConsumerHeroImageIndex
  heroSection?: AdminConsumerHeroSectionIndex
  sectionItem?: AdminConsumerHeroSectionItem
  landingPageSectionItemId?: number
  // Hero images already used as slides in this section, so "Add existing slide" can
  // warn when one is picked twice.
  sectionHeroImageIds?: number[]
  anchor?: 'left' | 'right'
}>(), {  })

const editItem: DropDownActionItem = {
  label: 'Edit',
  icon: faPen,
  onClick: () => heroImageDesign.openEdit({
    hero: __props.heroImage,
    heroSection: __props.heroSection,
    sectionItem: __props.sectionItem,
    landingPageSectionItemId: __props.landingPageSectionItemId,
  }),
}

const addSlideItem: DropDownActionItem = {
  label: 'Add new slide',
  icon: faPlus,
  onClick: () => heroImageDesign.openEdit({ heroSection: __props.heroSection, landingPageSectionItemId: __props.landingPageSectionItemId }),
}

const addExistingSlideItem: DropDownActionItem = {
  label: 'Add existing hero image',
  icon: faSquareDashedCirclePlus,
  onClick: () => heroSectionItemLogic.openAddExistingToSection({
    heroSection: __props.heroSection!,
    landingPageSectionItemId: __props.landingPageSectionItemId,
    sectionHeroImageIds: __props.sectionHeroImageIds,
  }),
}

const duplicateItem: DropDownActionItem = {
  label: 'Duplicate',
  icon: faCopy,
  onClick: () => heroImageDesign.openInlineDuplicate({
    hero: __props.heroImage,
    heroSection: __props.heroSection,
    sectionItem: __props.sectionItem,
    landingPageSectionItemId: __props.landingPageSectionItemId,
  }),
}

const openScheduleAndReorder = () => heroSectionLogic.openInlineEdit(__props.heroSection!, {
  landingPageSectionItemId: __props.landingPageSectionItemId,
  onSaved: () => window.location.reload(),
})

const groups: DropDownActionGroup[] = [
  {
    label: 'This slide',
    items: [
      editItem,
      duplicateItem,
      {
        label: 'Remove',
        icon: faTrash,
        danger: true,
        onClick: () => heroSectionItemLogic.confirmRemove(
          { ...__props.sectionItem!, sectionName: __props.heroSection!.sectionName },
          __props.heroSection,
          __props.landingPageSectionItemId,
        ),
      },
    ],
  },
  {
    label: 'This section',
    items: [
      addSlideItem,
      addExistingSlideItem,
      { label: 'Reorder slides', icon: faUpDown, onClick: openScheduleAndReorder },
      { label: 'Schedule slides', icon: faClock, onClick: openScheduleAndReorder },
    ],
  },
]

const items: DropDownActionItem[] = [editItem, duplicateItem]

const dropdownProps = computed(() =>
  __props.heroSection && __props.sectionItem ? { groups } : { items },
)
</script>

<template>
  <div class="inline-hero-actions">
    <DropDownActions v-bind="dropdownProps" :anchor="anchor ?? 'right'">
      <template #label>
        <div class="flex flex-row items-center gap-2">
          <GIcon :icon="faGear" size="sm"/>
          Modify
        </div>
      </template>
    </DropDownActions>
  </div>
</template>

<style lang="scss" scoped>
.inline-hero-actions {
  --drop-down-actions-menu-max-width: 400px;
  --drop-down-actions-trigger-bg: white;
  --drop-down-actions-trigger-border: 1px solid #e4eaf1;
  --drop-down-actions-trigger-border-radius: 12px;
  --drop-down-actions-trigger-color: var(--color-text-active);
  --drop-down-actions-trigger-font-weight: 600;
  --drop-down-actions-trigger-padding: 0.5rem 1rem;
  --drop-down-actions-trigger-hover-bg: var(--color-bg-surface-blue-hover);

  white-space: nowrap;
}
</style>
